import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';

import Abacus from 'components/common/icons/nav/abacus';
import AnswerPerformanceView from 'models/location/answer_performance_view';
import BookOpen from 'components/common/icons/nav/book_open';
import ChartBar from 'components/common/icons/nav/chart_bar';
import ChartLine from 'components/common/icons/nav/chart_line';
import ChevronDownIcon from 'components/lib/icons/chevron_down_icon';
import Cog from 'components/common/icons/nav/cog';
import CommentLines from 'components/common/icons/nav/comment_lines';
import Compass from 'components/common/icons/nav/compass';
import connect from 'components/lib/connect';
import GuidePerformanceView from 'models/location/guide_performance_view';
import Home from 'components/common/icons/nav/home';
import Inbox from 'models/location/inbox';
import KbAdmin from 'models/location/kb_admin';
import Link from 'components/common/link';
import LinkOutStroke from 'components/common/icons/stroke/link-out-stroke';
import List from 'components/common/icons/nav/list';
import Liveboards from 'models/location/liveboards';
import NavigateTo from 'actions/current_location/navigate_to';
import Plug from 'components/common/icons/nav/plug';
import Random from 'components/common/icons/nav/random';
import Reports from 'models/location/reports';
import ShareAlt from 'components/common/icons/nav/share_alt';
import Sitemap from 'components/common/icons/nav/sitemap';
import ThLarge from 'components/common/icons/nav/th_large';
import ThumbsUp from 'components/common/icons/nav/thumbs_up';
import WaveSquare from 'components/common/icons/nav/wave_square';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import { getLocationUrl } from 'scripts/adapters/routes/location_url';
import { HotkeysShownContext } from 'components/contexts/hotkeys';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function NavigationMenu({ hasGuideCollections, isSidekickEnabled, onClose }) {
  const { t } = useTranslation();
  const isFeatureEnabled = useIsFeatureEnabled();
  const { dispatchHotkeysShown } = useContext(HotkeysShownContext);
  const showHotkeys = useCallback(() => dispatchHotkeysShown('on'), [dispatchHotkeysShown]);
  const labsOn = useLabsToggle();

  // satisfies at least one feature
  const satisfiesFeatures = features => {
    if (typeof features === 'string') features = [features];
    return features.some(feature => isFeatureEnabled(feature));
  };

  function renderJourneysLink(opts = {}) {
    if (!isSidekickEnabled) {
      return null;
    }
    if (!satisfiesFeatures(['viewGuidePerformance', 'viewAnswerPerformance', 'answers:admin', 'answers:read'])) {
      return null;
    }
    const linkOpts =
      hasGuideCollections && satisfiesFeatures('viewGuidePerformance')
        ? { dataAid: 'guide-performance', locationModel: GuidePerformanceView }
        : { dataAid: 'answer-performance', locationModel: AnswerPerformanceView };

    return (
      <MenuOption
        data-aid={linkOpts.dataAid}
        icon={opts.icon}
        locationModel={linkOpts.locationModel}
        onClick={onClose}
        title={t('Journeys')}
      />
    );
  }

  if (isFeatureEnabled('newMainNav')) {
    return renderNewNavigation({
      isSidekickEnabled,
      labsOn,
      onClose,
      satisfiesFeatures,
      renderJourneysLink,
      showHotkeys,
      t,
    });
  }

  function renderSidekickMenuOptions() {
    if (!isSidekickEnabled || !satisfiesFeatures('sidekickConversationManagement')) {
      return null;
    }

    return (
      <>
        <StyledMenuItem data-aid="sidekick-conversations">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/communication-sessions"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/communication-sessions');
            }}
          >
            {t('Gladly AI Conversations')}
          </StyledLink>
        </StyledMenuItem>
        <StyledMenuItem data-aid="sidekick-reviews">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/reviews"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/reviews');
            }}
          >
            {t('Reviews')}
          </StyledLink>
        </StyledMenuItem>
        <StyledMenuItem data-aid="sidekick-guides">
          <StyledLink
            className="navigationMenu-option"
            href="/sidekick/guides"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/sidekick/guides');
            }}
          >
            {t('Guides')}
          </StyledLink>
        </StyledMenuItem>
      </>
    );
  }

  return (
    <div className={`navigationMenu navigationMenuV2`}>
      <StyledLine />
      {satisfiesFeatures(['conversation:read']) && (
        <MenuOption locationModel={Inbox} locationPath="" onClick={onClose} title={t('Home')} />
      )}
      {satisfiesFeatures(['answers:admin', 'answers:read']) && (
        <MenuOption locationModel={KbAdmin} onClick={onClose} title={t('Answers')} />
      )}

      {renderJourneysLink()}

      {renderSidekickMenuOptions()}

      {satisfiesFeatures('reports:read') && (
        <MenuOption locationModel={Reports} onClick={onClose} title={t('Reports')} />
      )}
      {satisfiesFeatures('viewLiveboards') && (
        <MenuOption locationModel={Liveboards} onClick={onClose} title={t('Liveboards')} />
      )}
      <StyledLine />
      {satisfiesFeatures([
        'adminSettings',
        'devManagement',
        'debuggingView',
        'answers:admin',
        'topics:admin',
        'auditLog:read',
      ]) && (
        <StyledMenuItem data-aid="settings-menu">
          <StyledLink
            className="navigationMenu-option"
            href="/admin/home"
            onClick={evt => {
              evt.preventDefault();
              window.location.assign('/admin/home');
            }}
          >
            {t('Settings')}
          </StyledLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-new-features"
          href="https://help.gladly.com/docs/whats-new"
          tabIndex="-1"
          target="release-notes"
        >
          {t("What's new in Gladly")}
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-help-link"
          href="https://help.gladly.com"
          tabIndex="-1"
          target="help"
        >
          {t('Help Docs')}
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      <StyledMenuItem>
        <StyledExternalLink
          className="navigationMenu-option"
          data-aid="navigationMenu-academy-link"
          href="https://academy.gladly.com"
          tabIndex="-1"
          target="academy"
        >
          {t('Gladly Academy')}
          <StyledLinkOutStroke />
        </StyledExternalLink>
      </StyledMenuItem>
      {satisfiesFeatures('contactSupport') && (
        <StyledMenuItem data-aid="contact-support-menu">
          <StyledExternalLink
            className="navigationMenu-option"
            data-aid="navigationMenu-help-link"
            href="/support.html"
            tabIndex="-1"
            target="support"
          >
            {t('Contact Support')}
            <StyledLinkOutStroke />
          </StyledExternalLink>
        </StyledMenuItem>
      )}
      <StyledLine />
      <StyledMenuItem>
        <StyledLink
          className="navigationMenu-option"
          data-aid="navigationMenu-shortcuts-link"
          onClick={showHotkeys}
          tabIndex="-1"
        >
          {t('Keyboard Shortcuts')}
        </StyledLink>
      </StyledMenuItem>
      <PrivacyPolicySection>
        <PrivacyPolicyLink
          data-aid="navigationMenu-privacy-policy-link"
          href="https://www.gladly.com/privacy-policy/"
          rel="noopener noreferrer"
          tabIndex="-1"
          target="_blank"
        >
          {t('Privacy Policy')}
        </PrivacyPolicyLink>
      </PrivacyPolicySection>
    </div>
  );
}

NavigationMenu.propTypes = {
  hasGuideCollections: PropTypes.bool.isRequired,
  isSidekickEnabled: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const NavigationMenuContainer = connect(mapStateToProps)(NavigationMenu);
export default NavigationMenuContainer;

function mapStateToProps({ getProvider }, { onClose }) {
  const organization = getProvider('organization').get();
  // Observe currentLocation so the menu re-renders on navigation. MenuOption's
  // active-state check reads window.location.pathname; without this
  // subscription the connect HOC's binding-observation can skip re-rendering
  // when the agent navigates, leaving the active highlight stale.
  const currentLocation = getProvider('currentLocation').get();
  return {
    onClose,
    hasGuideCollections: getProvider('guideCollections').findAll().length > 0,
    isSidekickEnabled: !!organization?.isSidekickEnabled,
    currentLocation,
  };
}

function renderNewNavigation({
  isSidekickEnabled,
  labsOn,
  onClose,
  satisfiesFeatures,
  renderJourneysLink,
  showHotkeys,
  t,
}) {
  const isExternalActive = path => window.location.pathname.startsWith(path);

  const goExternal = path => evt => {
    evt.preventDefault();
    window.location.assign(path);
  };

  const journeyItem = renderJourneysLink({ icon: Random });
  const showHome = satisfiesFeatures(['conversation:read']);
  const showAnswers = satisfiesFeatures(['answers:admin', 'answers:read']);
  const showLiveboards = satisfiesFeatures('viewLiveboards');
  const showReports = satisfiesFeatures('reports:read');
  const showSidekickItems = isSidekickEnabled && satisfiesFeatures('sidekickConversationManagement');
  const showSettings = satisfiesFeatures([
    'adminSettings',
    'devManagement',
    'debuggingView',
    'answers:admin',
    'topics:admin',
    'auditLog:read',
  ]);

  const hasTeam = showHome || showLiveboards || showAnswers || showReports;
  // The "Gladly AI" section is the Sidekick brand surface — gate the whole
  // tree on isSidekickEnabled so non-Sidekick orgs don't see an empty shell
  // of stub items.
  const hasGladlyAi = isSidekickEnabled;

  // Items the PR (sagansystems/glados#7765) gates by role rather than flag —
  // `teamManagement` is granted to team-manager / system-admin / compliance-admin,
  // matching the PR's `requires: ['system-admin', 'compliance-admin', 'team-manager']`.
  const showSignals = satisfiesFeatures('teamManagement');
  const showSimulations = showSidekickItems && satisfiesFeatures('teamManagement');

  const hasAnalyze = hasGladlyAi && (showSidekickItems || showSignals || !!journeyItem);
  const hasManage = hasGladlyAi && (showSidekickItems || showAnswers);
  const hasTest = hasGladlyAi && showSimulations;
  const hasDeploy = hasGladlyAi && showSidekickItems;

  return (
    <NewNavRoot aria-label={t('Main navigation')} className="navigationMenu navigationMenuV2">
      <StyledLine />

      {hasGladlyAi && (
        <CollapsibleSection
          headingId="nav-section-gladly-ai"
          listId="nav-section-gladly-ai-list"
          title={t('Gladly AI')}
        >
          {hasAnalyze && (
            <>
              <SubSectionHeading id="nav-subsection-analyze">
                <Trans>Analyze</Trans>
              </SubSectionHeading>
              <NewSubItemList aria-labelledby="nav-subsection-analyze">
                {showSidekickItems && (
                  <li>
                    <ExternalNavItem
                      dataAid="sidekick-conversations"
                      href="/sidekick/communication-sessions"
                      icon={CommentLines}
                      isActive={isExternalActive('/sidekick/communication-sessions')}
                      label={t('AI Conversations')}
                      onClick={goExternal('/sidekick/communication-sessions')}
                    />
                  </li>
                )}
                {showSidekickItems && (
                  <li>
                    <ExternalNavItem
                      dataAid="sidekick-reviews"
                      href="/sidekick/reviews"
                      icon={ThumbsUp}
                      isActive={isExternalActive('/sidekick/reviews')}
                      label={t('Feedback')}
                      onClick={goExternal('/sidekick/reviews')}
                    />
                  </li>
                )}
                {showSignals && (
                  <li>
                    <ExternalNavItem
                      dataAid="gladly-ai-signals"
                      href="/sidekick/signals"
                      icon={WaveSquare}
                      isActive={isExternalActive('/sidekick/signals')}
                      label={t('Signals')}
                      onClick={goExternal('/sidekick/signals')}
                    />
                  </li>
                )}
                {journeyItem && <li>{journeyItem}</li>}
              </NewSubItemList>
            </>
          )}

          {hasManage && (
            <>
              <SubSectionHeading id="nav-subsection-manage">
                <Trans>Manage</Trans>
              </SubSectionHeading>
              <NewSubItemList aria-labelledby="nav-subsection-manage">
                {showSidekickItems && (
                  <li>
                    <ExternalNavItem
                      dataAid="sidekick-guides"
                      href="/sidekick/guides"
                      icon={Compass}
                      isActive={isExternalActive('/sidekick/guides')}
                      label={t('AI Agents')}
                      onClick={goExternal('/sidekick/guides')}
                    />
                  </li>
                )}
                {showSidekickItems && (
                  <li>
                    <ExternalNavItem
                      dataAid="sidekick-workflows"
                      href="/sidekick/workflows"
                      icon={Sitemap}
                      isActive={isExternalActive('/sidekick/workflows')}
                      label={t('Workflows')}
                      onClick={goExternal('/sidekick/workflows')}
                    />
                  </li>
                )}
                {showAnswers && (
                  <li>
                    <MenuOption icon={BookOpen} locationModel={KbAdmin} onClick={onClose} title={t('Answers')} />
                  </li>
                )}
              </NewSubItemList>
            </>
          )}

          {hasTest && (
            <>
              <SubSectionHeading id="nav-subsection-test">
                <Trans>Test</Trans>
              </SubSectionHeading>
              <NewSubItemList aria-labelledby="nav-subsection-test">
                {showSimulations && (
                  <li>
                    <ExternalNavItem
                      dataAid="sidekick-simulations"
                      href="/sidekick/guides?panel=scenarios"
                      icon={Abacus}
                      isActive={
                        isExternalActive('/sidekick/guides') && window.location.search.includes('panel=scenarios')
                      }
                      label={t('Simulations')}
                      onClick={goExternal('/sidekick/guides?panel=scenarios')}
                    />
                  </li>
                )}
              </NewSubItemList>
            </>
          )}

          {hasDeploy && (
            <>
              <SubSectionHeading id="nav-subsection-deploy">
                <Trans>Deploy</Trans>
              </SubSectionHeading>
              <NewSubItemList aria-labelledby="nav-subsection-deploy">
                {showSidekickItems && (
                  <li>
                    <ExternalNavItem
                      dataAid="sidekick-changelog"
                      href="/sidekick/audit-history"
                      icon={List}
                      isActive={isExternalActive('/sidekick/audit-history')}
                      label={t('Changelog')}
                      onClick={goExternal('/sidekick/audit-history')}
                    />
                  </li>
                )}
                {showSidekickItems && (
                  <li>
                    <ExternalNavItem
                      dataAid="sidekick-entry-points"
                      href="/sidekick/entry-points"
                      icon={ShareAlt}
                      isActive={isExternalActive('/sidekick/entry-points')}
                      label={t('Entry Points')}
                      onClick={goExternal('/sidekick/entry-points')}
                    />
                  </li>
                )}
              </NewSubItemList>
            </>
          )}
        </CollapsibleSection>
      )}

      {hasTeam && (
        <CollapsibleSection headingId="nav-section-team" listId="nav-section-team-list" title={t('Gladly Team')}>
          {showHome && (
            <li>
              <MenuOption icon={Home} locationModel={Inbox} locationPath="" onClick={onClose} title={t('Home')} />
            </li>
          )}
          {showLiveboards && (
            <li>
              <MenuOption icon={ThLarge} locationModel={Liveboards} onClick={onClose} title={t('Liveboards')} />
            </li>
          )}
          {showAnswers && (
            <li>
              <MenuOption icon={BookOpen} locationModel={KbAdmin} onClick={onClose} title={t('Answers')} />
            </li>
          )}
          {showReports && (
            <li>
              <MenuOption icon={ChartLine} locationModel={Reports} onClick={onClose} title={t('Reports')} />
            </li>
          )}
        </CollapsibleSection>
      )}

      {showSettings && (
        <CollapsibleSection headingId="nav-section-admin" listId="nav-section-admin-list" title={t('Admin')}>
          <li>
            <ExternalNavItem
              dataAid="settings-menu"
              href="/admin/home"
              icon={Cog}
              isActive={isExternalActive('/admin')}
              label={t('Settings')}
              onClick={goExternal('/admin/home')}
            />
          </li>
          {isSidekickEnabled && (
            <li>
              <ExternalNavItem
                dataAid="sidekick-custom-actions"
                href="/sidekick/custom-actions"
                icon={Plug}
                isActive={isExternalActive('/sidekick/custom-actions')}
                label={t('Actions')}
                onClick={goExternal('/sidekick/custom-actions')}
              />
            </li>
          )}
        </CollapsibleSection>
      )}

      {satisfiesFeatures('userManagement') && (
        <CollapsibleSection
          headingId="nav-section-sagan-admin"
          listId="nav-section-sagan-admin-list"
          title={t('Sagan Admin')}
        >
          <li>
            <ExternalNavItem
              dataAid="sagan-admin-all-orgs"
              href="/sidekick/admin"
              isActive={
                isExternalActive('/sidekick/admin') && window.location.pathname.replace(/\/$/, '') === '/sidekick/admin'
              }
              label={t('All organizations')}
              onClick={goExternal('/sidekick/admin')}
            />
          </li>
          <li>
            <ExternalNavItem
              dataAid="sagan-admin-app-platform-actions"
              href="/sidekick/admin/app-platform-actions"
              isActive={isExternalActive('/sidekick/admin/app-platform-actions')}
              label={t('App platform actions')}
              onClick={goExternal('/sidekick/admin/app-platform-actions')}
            />
          </li>
          <li>
            <ExternalNavItem
              dataAid="sagan-admin-custom-actions"
              href="/sidekick/admin/custom-actions"
              isActive={isExternalActive('/sidekick/admin/custom-actions')}
              label={t('Custom actions')}
              onClick={goExternal('/sidekick/admin/custom-actions')}
            />
          </li>
          <li>
            <ExternalNavItem
              dataAid="sagan-admin-app-platform"
              href="/sidekick/admin/app-platform"
              isActive={isExternalActive('/sidekick/admin/app-platform')}
              label={t('App platform')}
              onClick={goExternal('/sidekick/admin/app-platform')}
            />
          </li>
          <li>
            <ExternalNavItem
              dataAid="sagan-admin-behavior-trees"
              href="/sidekick/admin/behavior-trees/configs"
              isActive={isExternalActive('/sidekick/admin/behavior-trees/configs')}
              label={t('Static configurations')}
              onClick={goExternal('/sidekick/admin/behavior-trees/configs')}
            />
          </li>
          {showAnswers && (
            <li>
              <MenuOption locationModel={KbAdmin} onClick={onClose} title={t('Knowledge')} />
            </li>
          )}
          <li>
            <ExternalNavItem
              dataAid="sagan-admin-organization"
              href="/sidekick/admin/edit"
              isActive={isExternalActive('/sidekick/admin/edit')}
              label={t('Organization')}
              onClick={goExternal('/sidekick/admin/edit')}
            />
          </li>
          {labsOn && (
            <li>
              <StubNavItem dataAid="sagan-admin-home-labs" icon={Home} label={t('Home (Labs)')} />
            </li>
          )}
          {labsOn && showSidekickItems && (
            <li>
              <ExternalNavItem
                dataAid="sagan-admin-explorer-labs"
                href="/sidekick/performance/explorer"
                icon={ChartBar}
                isActive={isExternalActive('/sidekick/performance/explorer')}
                label={t('Explorer (Labs)')}
                onClick={goExternal('/sidekick/performance/explorer')}
              />
            </li>
          )}
        </CollapsibleSection>
      )}

      <ResourcesSection showContactSupport={satisfiesFeatures('contactSupport')} />
      <KeyboardShortcutsList>
        <li>
          <StyledMenuItem>
            <StyledLink
              className="navigationMenu-option"
              data-aid="navigationMenu-shortcuts-link"
              onClick={showHotkeys}
              tabIndex="-1"
            >
              <Trans>Keyboard Shortcuts</Trans>
            </StyledLink>
          </StyledMenuItem>
        </li>
      </KeyboardShortcutsList>
      <NewPrivacyPolicySection>
        <PrivacyPolicyLink
          data-aid="navigationMenu-privacy-policy-link"
          href="https://www.gladly.com/privacy-policy/"
          rel="noopener noreferrer"
          tabIndex="-1"
          target="_blank"
        >
          <Trans>Privacy Policy</Trans>
          <VisuallyHidden>
            {' '}
            <Trans>(opens in new tab)</Trans>
          </VisuallyHidden>
        </PrivacyPolicyLink>
      </NewPrivacyPolicySection>
    </NewNavRoot>
  );
}

// SSR-safe read of the labs toggle. Hoisted to module scope so the effect
// below closes over a stable reference (keeps react-hooks/exhaustive-deps
// happy without needing useCallback inside the hook).
const readLabsToggle = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage?.getItem('glados-labs') === 'true';
};

// Labs toggle, mirroring the Glados sidekick app: persisted in
// `localStorage['glados-labs']` and toggled live via a `labs-changed` event
// (so dev tooling can flip it without a page reload). SSR-safe: starts off
// when window is undefined, then syncs after mount.
function useLabsToggle() {
  const [labsOn, setLabsOn] = useState(readLabsToggle);
  useEffect(() => {
    const sync = () => setLabsOn(readLabsToggle());
    sync();
    window.addEventListener('labs-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('labs-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return labsOn;
}

const NavIcon = ({ icon: IconComponent }) => (
  <NavIconBox aria-hidden="true">
    <IconComponent />
  </NavIconBox>
);

NavIcon.propTypes = {
  icon: PropTypes.elementType.isRequired,
};

const ExternalNavItem = ({ dataAid, href, icon, isActive, label, onClick }) => (
  <StyledMenuItem data-aid={dataAid} isActive={isActive}>
    <StyledLink
      aria-current={isActive ? 'page' : undefined}
      className="navigationMenu-option"
      href={href}
      onClick={onClick}
    >
      {icon && <NavIcon icon={icon} />}
      {label}
    </StyledLink>
  </StyledMenuItem>
);

ExternalNavItem.propTypes = {
  dataAid: PropTypes.string,
  href: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  isActive: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export const MenuOption = ({ icon, locationModel, onClick, title }) => {
  const executeAction = useExecuteAction();
  const handleClick = useCallback(
    evt => {
      evt.preventDefault();
      executeAction(NavigateTo, locationModel.create());
      onClick();
    },
    [executeAction, locationModel, onClick]
  );

  // Some location models (e.g. GuidePerformanceView) default query params, so
  // strip the query before segment comparison — otherwise the first path
  // segment would include "?..." and never match the bare pathname segment.
  const menuPath = getLocationUrl(locationModel.create()).split('?')[0];
  const pathname = window.location.pathname;
  // Director.js maps the root path "/" to the home action without rewriting the URL.
  // Treat "/" as "/home" so the Home menu item highlights when the agent lands on home.
  const isAtRoot = pathname === '/' || pathname === '';
  const currentLocation = isAtRoot ? 'home' : pathname.split('/')[1];
  const isActiveItem = menuPath.split('/')[1] === currentLocation;
  return (
    <StyledMenuItem isActive={isActiveItem}>
      <StyledLink
        aria-current={isActiveItem ? 'page' : undefined}
        className="navigationMenu-option"
        href={menuPath}
        onClick={handleClick}
      >
        {icon && <NavIcon icon={icon} />}
        {title}
      </StyledLink>
    </StyledMenuItem>
  );
};

MenuOption.propTypes = {
  icon: PropTypes.elementType,
  locationModel: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
};

const StubNavItem = ({ dataAid, icon, label }) => {
  // Placeholder for nav items whose backing routes don't exist yet. Renders a
  // visually identical link that no-ops on click — keeps the menu structure
  // stable while routes are wired up downstream.
  const handleClick = useCallback(evt => evt.preventDefault(), []);
  return (
    <StyledMenuItem data-aid={dataAid}>
      <StyledLink aria-disabled="true" className="navigationMenu-option" href="#" onClick={handleClick} role="link">
        <NavIcon icon={icon} />
        {label}
      </StyledLink>
    </StyledMenuItem>
  );
};

StubNavItem.propTypes = {
  dataAid: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
};

const CollapsibleSection = ({ children, defaultOpen = true, headingId, listId, title }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <>
      <SectionToggle
        aria-controls={listId}
        aria-expanded={isOpen}
        data-aid={`${headingId}-toggle`}
        id={headingId}
        onClick={() => setIsOpen(open => !open)}
        type="button"
      >
        {title}
        <SectionToggleChevron $isOpen={isOpen} aria-hidden="true">
          <ChevronDownIcon />
        </SectionToggleChevron>
      </SectionToggle>
      {isOpen && (
        <NewItemList aria-labelledby={headingId} id={listId}>
          {children}
        </NewItemList>
      )}
    </>
  );
};

CollapsibleSection.propTypes = {
  children: PropTypes.node,
  defaultOpen: PropTypes.bool,
  headingId: PropTypes.string.isRequired,
  listId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const ResourcesSection = ({ showContactSupport }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const headingId = 'nav-section-resources';
  const listId = 'nav-section-resources-list';
  return (
    <>
      <SectionToggle
        aria-controls={listId}
        aria-expanded={isOpen}
        data-aid="navigationMenu-resources-toggle"
        id={headingId}
        onClick={() => setIsOpen(open => !open)}
        type="button"
      >
        {t('Resources')}
        <SectionToggleChevron $isOpen={isOpen} aria-hidden="true">
          <ChevronDownIcon />
        </SectionToggleChevron>
      </SectionToggle>
      {isOpen && (
        <NewItemList aria-labelledby={headingId} id={listId}>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-new-features"
                href="https://help.gladly.com/docs/whats-new"
                tabIndex="-1"
                target="release-notes"
              >
                {t("What's new in Gladly")}
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-help-link"
                href="https://help.gladly.com"
                tabIndex="-1"
                target="help"
              >
                {t('Help Docs')}
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          <li>
            <StyledMenuItem>
              <StyledExternalLink
                className="navigationMenu-option"
                data-aid="navigationMenu-academy-link"
                href="https://academy.gladly.com"
                tabIndex="-1"
                target="academy"
              >
                {t('Gladly Academy')}
                <StyledLinkOutStroke aria-hidden="true" />
                <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
              </StyledExternalLink>
            </StyledMenuItem>
          </li>
          {showContactSupport && (
            <li>
              <StyledMenuItem data-aid="contact-support-menu">
                <StyledExternalLink
                  className="navigationMenu-option"
                  data-aid="navigationMenu-help-link"
                  href="/support.html"
                  tabIndex="-1"
                  target="support"
                >
                  {t('Contact Support')}
                  <StyledLinkOutStroke aria-hidden="true" />
                  <VisuallyHidden>{t('(opens in new tab)')}</VisuallyHidden>
                </StyledExternalLink>
              </StyledMenuItem>
            </li>
          )}
        </NewItemList>
      )}
    </>
  );
};

ResourcesSection.propTypes = {
  showContactSupport: PropTypes.bool.isRequired,
};

const ActiveItem = css`
  background-color: ${p => p.theme.colors.gray200};
  font-weight: bold;
  &:before {
    background-color: ${p => p.theme.colors.green500};
    bottom: 0;
    content: '';
    left: 0;
    margin: auto;
    position: absolute;
    top: 0;
    width: 4px;
  }
`;

const StyledLinkOutStroke = styled(LinkOutStroke)`
  height: 14px;
  width: 14px;
  margin-left: 4px;
`;

const NavIconBox = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.gray800};
  display: inline-flex;
  flex-shrink: 0;
  height: 24px;
  justify-content: center;
  margin-right: 4px;
  width: 27px;

  svg {
    fill: currentColor;
    height: 16px;
    width: 16px;

    path {
      fill: currentColor;
    }
  }
`;

// Flex column so the privacy policy can use margin-top: auto to anchor at the
// bottom of the menu when content is short, while still flowing in-line
// (and scrolling) when content is long. Avoids the legacy
// position: absolute approach where privacy policy floats over the items.
const NewNavRoot = styled.nav`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const NewItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  &:empty {
    display: none;
  }
`;

const SubSectionHeading = styled.h3`
  color: ${p => p.theme.colors.gray700};
  font-size: ${p => p.theme.fontSize.base};
  font-weight: bold;
  margin: 0;
  padding: ${p => p.theme.spacing.medium} ${p => p.theme.spacing.insetLarge} ${p => p.theme.spacing.small};
`;

const NewSubItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  &:empty {
    display: none;
  }

  a.navigationMenu-option {
    padding-left: 24px;
  }
`;

// Resources collapse/expand toggle. Headed as <h2> visually but rendered as a
// <button> inside it for keyboard a11y. We use a button element directly
// (instead of nesting <button> inside <h2>) because the toggle is the only
// labelable thing here and screen readers already announce the heading via
// aria-labelledby on the controlled list.
const SectionToggle = styled.button`
  align-items: center;
  background: none;
  border: 0;
  color: ${p => p.theme.colors.gray800};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: ${p => p.theme.fontSize.medium};
  font-weight: bold;
  justify-content: space-between;
  margin: 12px 0 0;
  padding: 8px 24px;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
`;

const SectionToggleChevron = styled.span`
  align-items: center;
  color: ${p => p.theme.colors.gray800};
  display: inline-flex;
  height: 12px;
  justify-content: center;
  margin-left: 8px;
  transform: rotate(${p => (p.$isOpen ? '0deg' : '-90deg')});
  transition: transform 0.15s ease;
  width: 12px;

  svg {
    fill: currentColor;
    height: 12px;
    width: 12px;

    path {
      fill: currentColor;
    }
  }
`;

// Keyboard Shortcuts is the first of the two bottom-anchored items
// (Keyboard Shortcuts + Privacy Policy). margin-top: auto inside the
// flex-column NewNavRoot pushes both to the bottom when content is short,
// while still flowing in-line (and scrolling) when content is long.
// Color is overridden to gray800 to match the Privacy Policy footer link.
const KeyboardShortcutsList = styled(NewItemList)`
  margin-top: auto;

  a,
  a:hover {
    color: ${p => p.theme.colors.gray800};
  }
`;

// Privacy policy sits directly under Keyboard Shortcuts (which has
// margin-top: auto), so the pair anchors to the bottom of the menu when
// content is short and falls back to in-flow scrolling when it's not.
const NewPrivacyPolicySection = styled.div`
  padding: 8px 0;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const StyledLink = styled(Link)`
  color: ${p => p.theme.colors.black};
  align-items: center;
  display: flex;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: none;
  }
`;

const StyledLine = styled.div`
  height: 1px;
  background-color: ${p => p.theme.colors.gray300};
  margin: 2px 0;

  & + & {
    display: none;
  }
`;

const StyledMenuItem = styled.div`
  cursor: pointer;
  padding: 0;
  position: relative;
  &:hover {
    background-color: ${p => p.theme.colors.green100};
  }
  ${p => p.isActive && ActiveItem}
`;

export const StyledExternalLink = styled.a`
  color: ${p => p.theme.colors.black};
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.green400};
    text-decoration: none;
  }
`;

const PrivacyPolicySection = styled.div`
  bottom: 10px;
  left: 0;
  width: 100%;
  padding: 8px 0;
  position: absolute;
`;

const PrivacyPolicyLink = styled.a`
  color: ${p => p.theme.colors.gray800};
  display: block;
  width: 100%;
  height: 32px;
  padding: 6px 24px;
  cursor: pointer;
  &:hover {
    color: ${p => p.theme.colors.gray800};
    text-decoration: none;
    background-color: ${p => p.theme.colors.green100};
  }
`;
